import React, { ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { AppBar, Slide, Toolbar } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import Box from "../Box";
import Button from "../Button";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

export interface DialogTitleProps {
    id: string;
    children?: ReactNode;
    onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        top: 8,
                        right: 8,
                        position: "absolute",
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

interface IProps {
    title?: string;
    closeButton?: string;
    buttonAction?: string;

    visible: boolean;
    dividers?: boolean;
    fullWidth?: boolean;
    fullScreen?: boolean;
    hasActionButton?: boolean;
    disabledActionButton?: boolean;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";

    children: ReactNode;

    onClose?: () => void;
    onAction?: () => void;
    onClickCloseIcon?: () => void;
    className?: string;
}

const Modal: React.FC<IProps> = ({
    title,
    children,
    visible,
    dividers,
    maxWidth,
    fullWidth,
    fullScreen,
    closeButton,
    buttonAction,
    hasActionButton,
    disabledActionButton,
    onClose,
    onAction,
    onClickCloseIcon,
    className,
}: IProps) => {
    const strings = useStrings();

    return (
        <>
            {fullScreen ? (
                <Dialog fullScreen open={visible} onClose={onClose} TransitionComponent={Transition} className={className}>
                    <AppBar sx={{ position: "relative", backgroundColor: "#ffffff" }}>
                        <Toolbar>
                            <Typography sx={{ flex: 1 }} variant="h6" component="div">
                                {title}
                            </Typography>

                            <Box gap={1} display="flex" justifyContent="flex-end" alignItems="flex-end" my={1}>
                                {Helpers.isFunction(onClose) && (
                                    <Button color="secondary" onClick={onClose}>
                                        {closeButton ? closeButton : strings.Common.CLOSE}
                                    </Button>
                                )}
                                {Helpers.isFunction(onAction) && (
                                    <Button onClick={onAction} disabled={disabledActionButton}>
                                        {buttonAction ? buttonAction : strings.Common.SAVE}
                                    </Button>
                                )}
                                {Helpers.isFunction(onClickCloseIcon) && (
                                    <IconButton onClick={onClickCloseIcon}>
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </Toolbar>
                    </AppBar>
                    <Box p={2}>{children}</Box>
                </Dialog>
            ) : (
                <BootstrapDialog
                    aria-labelledby="customized-dialog-title"
                    fullWidth={fullWidth}
                    maxWidth={maxWidth}
                    onClose={onClose}
                    open={visible}
                    className={className}
                >
                    {!Helpers.isNullOrEmpty(title) && (
                        <BootstrapDialogTitle id="customized-dialog-title" onClose={onClickCloseIcon ? onClickCloseIcon : onClose}>
                            {title}
                        </BootstrapDialogTitle>
                    )}
                    <DialogContent dividers={dividers}>{children}</DialogContent>
                    {hasActionButton && (
                        <DialogActions>
                            <Box gap={1} display="flex" justifyContent="flex-end" alignItems="flex-end" my={1}>
                                {Helpers.isFunction(onClose) && (
                                    <Button color="secondary" onClick={onClose}>
                                        {closeButton ? closeButton : strings.Common.CLOSE}
                                    </Button>
                                )}
                                {Helpers.isFunction(onAction) && (
                                    <Button onClick={onAction} disabled={disabledActionButton}>
                                        {buttonAction ? buttonAction : strings.Common.SAVE}
                                    </Button>
                                )}
                            </Box>
                        </DialogActions>
                    )}
                </BootstrapDialog>
            )}
        </>
    );
};

export default Modal;
